import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

// Cancelled orders: shows the Shopify full-refund outcome and lets Back Office
// retry when it failed or never ran (e.g. No Mold refund unticked by mistake).
export function ShopifyRefundStrip({ order, user }) {
  const qc = useQueryClient();
  const retry = useMutation({
    mutationFn: () => api.post(`/orders/${order.id}/shopify-refund`).then((r) => r.data),
    onSuccess: (sr) => {
      qc.invalidateQueries();
      if (sr?.error) toast.error(`Shopify refund failed: ${sr.error}`, { duration: 12000 });
      else toast.success(`Shopify refund ${sr.status}${sr.refunded_amount ? ` · ${sr.refunded_amount} ${sr.currency || ""}` : ""}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Shopify refund failed"),
  });
  if (!["back_office", "super_admin"].includes(user?.role)) return null;
  const refundable = order?.cancelled || order?.status === "cancelled" || order?.refund_id || order?.last_claim_status === "refund";
  if (!refundable || !order?.shopify_order_id) return null;
  const sr = order.shopify_refund;
  const refundedNum = Number(sr?.refunded_amount || 0);
  const totalNum = Number(sr?.order_total || 0);
  const fullyRefunded = sr?.fully_refunded || ["REFUNDED", "VOIDED"].includes(String(sr?.financial_status || "").toUpperCase()) || (totalNum > 0 && refundedNum >= totalNum - 0.01);
  const done = sr?.status === "done" && (fullyRefunded || !sr?.order_total);
  const partialOnly = sr?.status === "done" && !done;
  const pending = Number(sr?.pending_amount || 0) > 0;
  const tone = done ? "border-emerald-300 bg-emerald-50 text-emerald-900" : (sr?.error || partialOnly) ? "border-rose-300 bg-rose-50 text-rose-900" : "border-slate-300 bg-slate-50 text-slate-800";
  const headline = done ? `Shopify refunded ${sr.refunded_amount || ""} ${sr.currency || ""}${pending ? " · processing" : ""}`.trim()
    : partialOnly ? `Shopify shows only ${sr.refunded_amount || 0} of ${sr.order_total} ${sr.currency || ""} refunded — the customer has NOT been refunded in full`
    : sr?.error ? `Shopify refund failed — ${sr.error}`
    : sr?.status === "pending" ? "Shopify refund in progress…"
    : order?.refund_id && !order?.cancelled ? "No Shopify refund issued for this claim refund" : "No Shopify refund issued for this cancellation";
  return (
    <div className={`rounded-md border px-4 py-2 text-sm flex flex-wrap items-center gap-3 ${tone}`} data-testid="shopify-refund-strip">
      <span className="font-semibold">💳 {headline}</span>
      {sr?.financial_status && <span className="text-xs font-mono opacity-80">{sr.financial_status}</span>}
      {sr?.at && <span className="text-xs opacity-70">{String(sr.at).slice(0, 16).replace("T", " ")} · {sr.by}</span>}
      {!done && (
        <Button size="sm" className="ml-auto h-8 bg-emerald-600 hover:bg-emerald-700 text-white" disabled={retry.isPending}
          data-testid="shopify-refund-retry-btn"
          onClick={() => { if (window.confirm(`Refund ${order.po_number} in FULL to the customer's original payment method in Shopify${order.fulfilled_at ? "" : " (and cancel it there)"}?`)) retry.mutate(); }}>
          {retry.isPending ? "Refunding…" : (sr?.error || partialOnly) ? "↻ Retry Shopify refund" : "💳 Refund in Shopify now"}
        </Button>
      )}
    </div>
  );
}
